import React from 'react';
import { CosmicWaves } from '@/components/visualizers/CosmicWaves';
import { MirrorSpectrum } from '@/components/visualizers/MirrorSpectrum';
import { FrostyWaves } from '@/components/visualizers/FrostyWaves';
import { DualCore } from '@/components/visualizers/DualCore';
import { AmbientSpectrum } from '@/components/visualizers/AmbientSpectrum';
import { SandyDunes } from '@/components/visualizers/SandyDunes';
import { CityWarp } from '@/components/visualizers/CityWarp';

// Global state for randomizer
let randomizeState = {
  currentVisualizerIndex: 0,
  nextVisualizerIndex: 1,
  lastSwitchTime: 0,
  switchDuration: 20, // Base duration in seconds
  crossfadeProgress: 0,
  crossfadeDuration: 2, // Crossfade duration in seconds
  isTransitioning: false,
  initialized: false
};

// Available visualizers for randomization
const availableVisualizers = [
  { component: CosmicWaves, name: 'Cosmic Waves' },
  { component: MirrorSpectrum, name: 'Mirror Spectrum' },
  { component: FrostyWaves, name: 'Frosty Waves' },
  { component: DualCore, name: 'Dual Core' },
  { component: AmbientSpectrum, name: 'Ambient Spectrum' },
  { component: SandyDunes, name: 'Sandy Dunes' },
  { component: CityWarp, name: 'City Warp' }
];

function initializeRandomizer() {
  if (!randomizeState.initialized) {
    randomizeState.currentVisualizerIndex = Math.floor(Math.random() * availableVisualizers.length);
    randomizeState.nextVisualizerIndex = (randomizeState.currentVisualizerIndex + 1) % availableVisualizers.length;
    randomizeState.lastSwitchTime = Date.now() * 0.001;
    randomizeState.switchDuration = 15 + Math.random() * 10; // 15-25 seconds
    randomizeState.initialized = true;
    console.log('🎲 Randomizer initialized with:', availableVisualizers[randomizeState.currentVisualizerIndex].name);
  }
}

function updateRandomizerState(time) {
  const timeSinceLastSwitch = time - randomizeState.lastSwitchTime;
  
  // Check if it's time to start crossfade
  if (!randomizeState.isTransitioning && timeSinceLastSwitch >= randomizeState.switchDuration) {
    console.log('🎲 Starting crossfade transition...');
    randomizeState.isTransitioning = true;
    randomizeState.crossfadeProgress = 0;
    
    // Select next random visualizer (ensure it's different from current)
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * availableVisualizers.length);
    } while (nextIndex === randomizeState.currentVisualizerIndex && availableVisualizers.length > 1);
    
    randomizeState.nextVisualizerIndex = nextIndex;
    console.log('🎲 Transitioning from:', availableVisualizers[randomizeState.currentVisualizerIndex].name, 
                'to:', availableVisualizers[randomizeState.nextVisualizerIndex].name);
  }
  
  // Update crossfade progress
  if (randomizeState.isTransitioning) {
    const crossfadeTime = timeSinceLastSwitch - randomizeState.switchDuration;
    randomizeState.crossfadeProgress = Math.min(crossfadeTime / randomizeState.crossfadeDuration, 1);
    
    // Complete transition
    if (randomizeState.crossfadeProgress >= 1) {
      console.log('🎲 Transition complete. Now showing:', availableVisualizers[randomizeState.nextVisualizerIndex].name);
      randomizeState.currentVisualizerIndex = randomizeState.nextVisualizerIndex;
      randomizeState.isTransitioning = false;
      randomizeState.lastSwitchTime = time;
      randomizeState.switchDuration = 15 + Math.random() * 10; // Randomize next duration (15-25 seconds)
      randomizeState.crossfadeProgress = 0;
    }
  }
}

function drawCrossfadedVisualizers(ctx, dataArray, width, height, isPlaying, time) {
  const currentVisualizer = availableVisualizers[randomizeState.currentVisualizerIndex];
  
  if (!randomizeState.isTransitioning) {
    // No transition - just draw current visualizer
    currentVisualizer.component({ ctx, dataArray, width, height, isPlaying });
  } else {
    // Crossfade transition between visualizers
    const nextVisualizer = availableVisualizers[randomizeState.nextVisualizerIndex];
    
    // Create temporary canvases for each visualizer
    const tempCanvas1 = document.createElement('canvas');
    const tempCanvas2 = document.createElement('canvas');
    tempCanvas1.width = width;
    tempCanvas1.height = height;
    tempCanvas2.width = width;
    tempCanvas2.height = height;
    
    const tempCtx1 = tempCanvas1.getContext('2d');
    const tempCtx2 = tempCanvas2.getContext('2d');
    
    // Draw current visualizer to first temp canvas
    currentVisualizer.component({ 
      ctx: tempCtx1, 
      dataArray, 
      width, 
      height, 
      isPlaying 
    });
    
    // Draw next visualizer to second temp canvas
    nextVisualizer.component({ 
      ctx: tempCtx2, 
      dataArray, 
      width, 
      height, 
      isPlaying 
    });
    
    // Clear main canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw current visualizer with decreasing opacity
    ctx.save();
    ctx.globalAlpha = 1 - randomizeState.crossfadeProgress;
    ctx.drawImage(tempCanvas1, 0, 0);
    ctx.restore();
    
    // Draw next visualizer with increasing opacity
    ctx.save();
    ctx.globalAlpha = randomizeState.crossfadeProgress;
    ctx.drawImage(tempCanvas2, 0, 0);
    ctx.restore();
    
    // Add crossfade effect overlay
    drawCrossfadeEffect(ctx, width, height, randomizeState.crossfadeProgress, time);
  }
}

function drawCrossfadeEffect(ctx, width, height, progress, time) {
  // Subtle particle effect during crossfade
  ctx.save();
  
  const particleCount = Math.floor(progress * 20);
  for (let i = 0; i < particleCount; i++) {
    const x = Math.random() * width;
    const y = Math.random() * height;
    const size = 1 + Math.random() * 3;
    const hue = (time * 60 + i * 30) % 360;
    const alpha = progress * (0.3 + Math.random() * 0.4);
    
    ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // Subtle wave effect during transition
  if (progress > 0.3 && progress < 0.7) {
    const waveAlpha = Math.sin((progress - 0.3) / 0.4 * Math.PI) * 0.2;
    ctx.strokeStyle = `hsla(${(time * 40) % 360}, 60%, 60%, ${waveAlpha})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    for (let x = 0; x <= width; x += 4) {
      const y = height/2 + Math.sin(x * 0.02 + time * 3) * 30 * progress;
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }
  
  ctx.restore();
}

export function drawRandomizeVisualizer(ctx, dataArray, width, height, isPlaying) {
  const time = Date.now() * 0.001;
  
  // Initialize randomizer on first run
  initializeRandomizer();
  
  // Update randomizer state
  updateRandomizerState(time);
  
  // Draw visualizers with crossfade
  drawCrossfadedVisualizers(ctx, dataArray, width, height, isPlaying, time);
  
  // Debug info (can be removed in production)
  if (randomizeState.isTransitioning) {
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.font = '12px monospace';
    ctx.fillText(`Crossfade: ${Math.round(randomizeState.crossfadeProgress * 100)}%`, 10, 20);
    ctx.fillText(`From: ${availableVisualizers[randomizeState.currentVisualizerIndex].name}`, 10, 35);
    ctx.fillText(`To: ${availableVisualizers[randomizeState.nextVisualizerIndex].name}`, 10, 50);
    ctx.restore();
  }
}

// Reset function to reinitialize the randomizer
export function resetRandomizer() {
  randomizeState.initialized = false;
  randomizeState.isTransitioning = false;
  randomizeState.crossfadeProgress = 0;
  console.log('🎲 Randomizer reset');
}

// Export the component function that matches the expected interface
export function RandomizeVisualizer({ ctx, dataArray, width, height, isPlaying }) {
  drawRandomizeVisualizer(ctx, dataArray, width, height, isPlaying);
}